import React, { useState } from 'react';
import { Typography, Spin } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, LoadingOutlined } from '@ant-design/icons';
import { calculateAbsoluteDifference } from '../charts/benchmarkThresholds';

const { Text } = Typography;

interface MiniOrbitalHeroProps {
    brandEquityScore: number;
    brandEquityClassificationLabel?: string;
    brandLoyaltyScore: number | null;
    brandLoyaltyClassificationLabel?: string;
    perceivedQualityScore: number | null;
    perceivedQualityClassificationLabel?: string;
    totalResponses: number;
    previousPeriodScore: number | null;
    benchmarkComparison: { industryMean: number } | null;
    insufficientSampleSize?: boolean;
    isCalculating?: boolean;
    brandName: string;
    elementDescriptions?: Record<string, { mouseover?: string; sidebar?: string }>;
    periodLabel?: string;
    onNavigateToDataSelection: () => void;
    onNavigateToViz: () => void;
    onNavigateToTrends: () => void;
    onNavigateToBenchmarking: () => void;
}

type HoveredElement = 'Brand Equity' | 'Brand Loyalty' | 'Perceived Quality' | null;

const MiniOrbitalHero: React.FC<MiniOrbitalHeroProps> = ({
    brandEquityScore,
    brandEquityClassificationLabel,
    brandLoyaltyScore,
    brandLoyaltyClassificationLabel,
    perceivedQualityScore,
    perceivedQualityClassificationLabel,
    totalResponses,
    previousPeriodScore,
    benchmarkComparison,
    isCalculating,
    brandName,
    elementDescriptions = {},
    periodLabel,
    onNavigateToDataSelection,
    onNavigateToViz,
    onNavigateToTrends,
    onNavigateToBenchmarking,
}) => {
    const svgWidth = 750;
    const svgHeight = 230;
    const centerX = svgWidth / 2;
    const centerY = 100;

    const mainRadius = 90;
    const sideRadius = 62;

    const leftCircleX = centerX - 250;
    const rightCircleX = centerX + 250;

    const [hoveredElement, setHoveredElement] = useState<HoveredElement>(null);

    const getScoreForElement = (element: HoveredElement): number | null => {
        switch (element) {
            case 'Brand Equity': return brandEquityScore;
            case 'Brand Loyalty': return brandLoyaltyScore;
            case 'Perceived Quality': return perceivedQualityScore;
            default: return null;
        }
    };

    const getScoreColor = (element: HoveredElement): string => {
        if (element === 'Brand Equity') return '#3d5a80';
        return '#3d5a80';
    };

    const getElementTypeLabel = (element: HoveredElement): string => {
        if (element === 'Brand Equity') return 'Brand Equity';
        return 'Integral Element';
    };

    const prevDiff = previousPeriodScore && previousPeriodScore !== -999
        ? brandEquityScore - previousPeriodScore
        : null;

    const benchDiff = benchmarkComparison
        ? calculateAbsoluteDifference(brandEquityScore, benchmarkComparison.industryMean)
        : null;

    const formatDiff = (diff: number) => {
        const sign = diff >= 0 ? '+' : '';
        return `${sign}${diff.toFixed(1)}%`;
    };

    const getDiffColor = (diff: number) => diff >= 0 ? '#059669' : '#dc6803';

    return (
        <div
            onClick={onNavigateToViz}
            style={{
                background: '#f0f4f8',
                borderRadius: '20px',
                padding: '28px 32px 24px',
                marginBottom: '24px',
                border: '2px solid #3d5a80',
                boxShadow: '0 10px 40px rgba(61, 90, 128, 0.12), 0 1px 3px rgba(61, 90, 128, 0.08)',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s ease',
                position: 'relative' as const,
                overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 16px 56px rgba(61, 90, 128, 0.22), 0 1px 3px rgba(61, 90, 128, 0.08)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 40px rgba(61, 90, 128, 0.12), 0 1px 3px rgba(61, 90, 128, 0.08)';
                setHoveredElement(null);
            }}
        >

            {isCalculating && (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 42, color: '#3d5a80' }} spin />} />
                    <Text style={{ display: 'block', marginTop: 16, color: '#2c4a6e', fontSize: 16 }}>
                        Calculating brand equity...
                    </Text>
                </div>
            )}
            {!isCalculating && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' as const }}>
                    {/* Title */}
                    <div style={{ marginBottom: 12, textAlign: 'center' }}>
                        <span style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: '#1b2a41',
                            letterSpacing: '-0.02em',
                        }}>
                            {brandName}
                            <span style={{ color: '#3d5a80', fontWeight: 400, marginLeft: 6 }}>
                                &rsquo;s
                            </span>
                        </span>
                        <span style={{
                            fontSize: 24,
                            fontWeight: 400,
                            color: '#3d5a80',
                            marginLeft: 8,
                            letterSpacing: '-0.01em',
                        }}>
                            Brand Equity Snapshot
                        </span>
                    </div>

                    {/* SVG Orbital */}
                    <svg
                        width="100%"
                        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                        style={{ maxWidth: svgWidth, display: 'block' }}
                    >
                        <defs />

                        {/* Connecting orbital ring */}
                        <ellipse
                            cx={centerX}
                            cy={centerY}
                            rx={265}
                            ry={65}
                            fill="none"
                            stroke="#d1d5db"
                            strokeWidth={1}
                            strokeDasharray="8 5"
                            opacity={0.5}
                        />

                        {/* Connection lines (input links, same green as V3) */}
                        <line
                            x1={leftCircleX + sideRadius}
                            y1={centerY}
                            x2={centerX - mainRadius}
                            y2={centerY}
                            stroke="rgba(61, 90, 128, 0.6)"
                            strokeWidth="2.5"
                        />
                        <line
                            x1={rightCircleX - sideRadius}
                            y1={centerY}
                            x2={centerX + mainRadius}
                            y2={centerY}
                            stroke="rgba(61, 90, 128, 0.6)"
                            strokeWidth="2.5"
                        />

                        {/* Left circle - Brand Loyalty */}
                        <text
                            x={leftCircleX}
                            y={centerY - sideRadius - 14}
                            textAnchor="middle"
                            fill="#6b7280"
                            fontSize="13"
                            fontWeight="600"
                            letterSpacing="0.03em"
                            style={{ pointerEvents: 'none' } as React.CSSProperties}
                        >
                            Brand Loyalty
                        </text>
                        <circle
                            cx={leftCircleX}
                            cy={centerY}
                            r={sideRadius}
                            fill="#3d5a80"
                            opacity={brandLoyaltyScore !== null ? 1 : 0.3}
                        />
                        <text
                            x={leftCircleX}
                            y={centerY + 1}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="white"
                            fontSize="28"
                            fontWeight="700"
                            style={{ pointerEvents: 'none' } as React.CSSProperties}
                        >
                            {brandLoyaltyScore !== null ? `${Math.round(brandLoyaltyScore)}%` : 'N/A'}
                        </text>
                        {/* Hover target for Brand Loyalty */}
                        <circle
                            cx={leftCircleX}
                            cy={centerY}
                            r={sideRadius}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredElement('Brand Loyalty')}
                            onMouseLeave={() => setHoveredElement(null)}
                        />

                        {/* Center circle - Brand Equity */}
                        <circle
                            cx={centerX}
                            cy={centerY}
                            r={mainRadius}
                            fill="#3d5a80"
                        />
                        <text
                            x={centerX}
                            y={centerY - 8}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="white"
                            fontSize="44"
                            fontWeight="700"
                            style={{ pointerEvents: 'none' } as React.CSSProperties}
                        >
                            {Math.round(brandEquityScore)}%
                        </text>
                        <text
                            x={centerX}
                            y={centerY + 28}
                            textAnchor="middle"
                            fill="rgba(255,255,255,0.85)"
                            fontSize="13"
                            fontWeight="500"
                            letterSpacing="0.05em"
                            style={{ pointerEvents: 'none' } as React.CSSProperties}
                        >
                            Brand Equity
                        </text>
                        {/* Hover target for Brand Equity */}
                        <circle
                            cx={centerX}
                            cy={centerY}
                            r={mainRadius}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredElement('Brand Equity')}
                            onMouseLeave={() => setHoveredElement(null)}
                        />

                        {/* Right circle - Perceived Quality */}
                        <text
                            x={rightCircleX}
                            y={centerY - sideRadius - 14}
                            textAnchor="middle"
                            fill="#6b7280"
                            fontSize="13"
                            fontWeight="600"
                            letterSpacing="0.03em"
                            style={{ pointerEvents: 'none' } as React.CSSProperties}
                        >
                            Perceived Quality
                        </text>
                        <circle
                            cx={rightCircleX}
                            cy={centerY}
                            r={sideRadius}
                            fill="#3d5a80"
                            opacity={perceivedQualityScore !== null ? 1 : 0.3}
                        />
                        <text
                            x={rightCircleX}
                            y={centerY + 1}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="white"
                            fontSize="28"
                            fontWeight="700"
                            style={{ pointerEvents: 'none' } as React.CSSProperties}
                        >
                            {perceivedQualityScore !== null ? `${Math.round(perceivedQualityScore)}%` : 'N/A'}
                        </text>
                        {/* Hover target for Perceived Quality */}
                        <circle
                            cx={rightCircleX}
                            cy={centerY}
                            r={sideRadius}
                            fill="transparent"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredElement('Perceived Quality')}
                            onMouseLeave={() => setHoveredElement(null)}
                        />
                    </svg>

                    {/* Scale bar + classification row, aligned under each circle */}
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        maxWidth: svgWidth,
                        marginTop: '-20px',
                    }}>
                        {([
                            { score: brandLoyaltyScore, label: brandLoyaltyClassificationLabel },
                            { score: brandEquityScore, label: brandEquityClassificationLabel },
                            { score: perceivedQualityScore, label: perceivedQualityClassificationLabel },
                        ] as const).map((col, i) => (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 20px' }}>
                                {col.score != null && (
                                    <>
                                        <div className="mini-orbital-scale" style={{ position: 'relative', width: '100%', maxWidth: 160, height: '8px' }}>
                                            <div style={{
                                                position: 'absolute',
                                                inset: 0,
                                                background: 'linear-gradient(to right, #ef4444 0%, #f59e0b 25%, #eab308 40%, #84cc16 60%, #22c55e 75%, #10b981 100%)',
                                                borderRadius: '4px',
                                                border: '1px solid rgba(0,0,0,0.1)',
                                                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)',
                                            }} />
                                            {/* Zero marker */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '50%',
                                                top: '-1px',
                                                bottom: '-1px',
                                                width: '1px',
                                                backgroundColor: 'rgba(0,0,0,0.4)',
                                                transform: 'translateX(-50%)',
                                            }} />
                                            {/* Position indicator */}
                                            <div style={{
                                                position: 'absolute',
                                                left: `${((col.score + 100) / 200) * 100}%`,
                                                top: '-4px',
                                                width: '3px',
                                                height: '16px',
                                                backgroundColor: '#1f2937',
                                                borderRadius: '2px',
                                                transform: 'translateX(-50%)',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                            }} />
                                        </div>
                                        <div className="mini-orbital-scale" style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            width: '100%',
                                            maxWidth: 160,
                                            fontSize: '10px',
                                            color: '#6b7280',
                                            marginTop: '6px',
                                        }}>
                                            <span>-100%</span>
                                            <span>100%</span>
                                        </div>
                                        {col.label && (
                                            <div className="mini-orbital-classification" style={{
                                                marginTop: '6px',
                                                fontSize: i === 1 ? '13px' : '11px',
                                                fontWeight: 700,
                                                color: i === 1 ? '#1b2a41' : '#3d5a80',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.03em',
                                                ...(i === 1 ? {
                                                    background: 'rgba(61, 90, 128, 0.1)',
                                                    padding: '3px 12px',
                                                    borderRadius: '6px',
                                                } : {}),
                                            }}>
                                                {col.label}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Stats row */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '24px',
                        flexWrap: 'wrap',
                        marginTop: '14px',
                        padding: '10px 20px',
                        background: 'rgba(61, 90, 128, 0.04)',
                        borderRadius: '12px',
                        border: '1px solid rgba(61, 90, 128, 0.1)',
                    }}>
                        {/* Responses + Period */}
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                            <span style={{ fontSize: 24, fontWeight: 700, color: '#1b2a41', letterSpacing: '-0.02em' }}>
                                {totalResponses.toLocaleString()}
                            </span>
                            <span style={{ fontSize: 14, color: '#3d5a80', fontWeight: 500 }}>
                                responses
                            </span>
                            <span
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNavigateToDataSelection();
                                }}
                                title="Change period"
                                style={{
                                    fontSize: 12, color: '#3d5a80', marginLeft: 4,
                                    background: 'rgba(61,90,128,0.1)', padding: '2px 8px', borderRadius: 6,
                                    cursor: 'pointer', transition: 'all 0.2s ease',
                                    borderBottom: '1px dashed rgba(61,90,128,0.3)',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(61,90,128,0.18)';
                                    e.currentTarget.style.color = '#2c4a6e';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(61,90,128,0.1)';
                                    e.currentTarget.style.color = '#3d5a80';
                                }}
                            >
                                {periodLabel || 'Last 30 days'}
                            </span>
                        </div>

                        {/* Divider */}
                        {(prevDiff !== null || benchDiff !== null) && (
                            <div style={{ width: 1, height: 20, background: 'rgba(61, 90, 128, 0.2)' }} />
                        )}

                        {/* vs Previous */}
                        {prevDiff !== null && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNavigateToTrends();
                                }}
                                style={{
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '2px 10px', borderRadius: 8,
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(61,90,128,0.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <span style={{ fontSize: 13, color: '#78716c', fontWeight: 500 }}>vs Prev</span>
                                {prevDiff >= 0 ? (
                                    <ArrowUpOutlined style={{ color: getDiffColor(prevDiff), fontSize: 12 }} />
                                ) : (
                                    <ArrowDownOutlined style={{ color: getDiffColor(prevDiff), fontSize: 12 }} />
                                )}
                                <span style={{ color: getDiffColor(prevDiff), fontSize: 15, fontWeight: 700 }}>
                                    {formatDiff(prevDiff)}
                                </span>
                            </div>
                        )}

                        {/* vs Benchmark */}
                        {benchDiff !== null && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onNavigateToBenchmarking();
                                }}
                                style={{
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '2px 10px', borderRadius: 8,
                                    transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(61,90,128,0.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                <span style={{ fontSize: 13, color: '#78716c', fontWeight: 500 }}>vs Benchmark</span>
                                {benchDiff >= 0 ? (
                                    <ArrowUpOutlined style={{ color: getDiffColor(benchDiff), fontSize: 12 }} />
                                ) : (
                                    <ArrowDownOutlined style={{ color: getDiffColor(benchDiff), fontSize: 12 }} />
                                )}
                                <span style={{ color: getDiffColor(benchDiff), fontSize: 15, fontWeight: 700 }}>
                                    {formatDiff(benchDiff)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Fixed tooltip - top left, like orbital v3 */}
            <div style={{
                position: 'absolute',
                top: 16,
                left: 16,
                width: 220,
                padding: '14px 16px',
                background: 'rgba(240, 244, 248, 0.97)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                fontSize: '13px',
                pointerEvents: 'none',
                zIndex: 10,
                boxShadow: '0 4px 16px rgba(61, 90, 128, 0.15)',
                border: '1px solid rgba(61, 90, 128, 0.25)',
                opacity: hoveredElement ? 1 : 0,
                transition: 'opacity 0.2s',
            }}>
                {hoveredElement && (
                    <>
                        <div style={{
                            fontWeight: 700,
                            marginBottom: 6,
                            fontSize: '15px',
                            color: '#1b2a41',
                        }}>
                            {hoveredElement}
                        </div>
                        {elementDescriptions[hoveredElement]?.mouseover && (
                            <div style={{
                                marginBottom: 8,
                                fontSize: '11.5px',
                                color: '#3d5a80',
                                fontStyle: 'italic',
                                lineHeight: 1.4,
                            }}>
                                {elementDescriptions[hoveredElement].mouseover}
                            </div>
                        )}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <span style={{ color: '#3d5a80', fontSize: '12px' }}>Score:</span>
                            {getScoreForElement(hoveredElement) !== null ? (
                                <span style={{
                                    color: getScoreColor(hoveredElement),
                                    fontWeight: 700,
                                    fontSize: '22px',
                                    letterSpacing: '-0.02em',
                                }}>
                                    {getScoreForElement(hoveredElement)!.toFixed(1)}%
                                </span>
                            ) : (
                                <span style={{ color: '#3d5a80', fontWeight: 600, fontSize: '16px' }}>N/A</span>
                            )}
                        </div>
                        <div style={{
                            marginTop: 6,
                            paddingTop: 6,
                            borderTop: '1px solid rgba(61, 90, 128, 0.15)',
                            fontSize: '11px',
                            color: '#3d5a80',
                        }}>
                            {getElementTypeLabel(hoveredElement)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default MiniOrbitalHero;
